<template>
  <div class="login-background" >
    <div class="flex min-h-full flex-1 flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div class="sm:mx-auto sm:w-full sm:max-w-md">
        <!-- <img class="mx-auto h-10 w-auto" src="https://firebasestorage.googleapis.com/v0/b/mesh-db-stg.appspot.com/o/icons%2Fmeshcomm-icon.png?alt=media&token=44dedc55-ef54-4256-a204-de8cd84829db" alt="Meshportal icon" /> -->
        <h2 class="mt-6 text-center text-2xl font-bold leading-9 tracking-tight text-white">Meshportal Login</h2>
      </div>

      <div class="mt-10 sm:mx-auto sm:w-full sm:max-w-[480px]">
        <div class="bg-mc-white px-6 py-12 shadow-mc-sm sm:rounded-mc-md sm:px-12">
          <form class="space-y-6" @submit.prevent="login">
            <div>
              <label for="email" class="block text-xs font-semibold text-mc-ink mb-1">Email address</label>
              <div class="mt-1">
                <input id="email" name="email" type="email" autocomplete="email" v-model="email" required="" class="block w-full rounded-mc-sm border border-mc-steel bg-mc-white py-1.5 px-3 text-sm text-mc-ink focus:outline-none focus:ring-2 focus:ring-mc-blue/40" />
              </div>
            </div>

            <div>
              <label for="password" class="block text-xs font-semibold text-mc-ink mb-1">Password</label>
              <div class="mt-1">
                <input id="password" name="password" type="password" autocomplete="current-password" v-model="password" required="" class="block w-full rounded-mc-sm border border-mc-steel bg-mc-white py-1.5 px-3 text-sm text-mc-ink focus:outline-none focus:ring-2 focus:ring-mc-blue/40" />
              </div>
            </div>

            <div class="flex items-center justify-between">
              <div class="text-sm leading-6">
                <a id="forgotPassword" href="#" @click="forgotPassword" class="font-semibold text-mc-red hover:text-mc-red-700 transition-colors duration-160">Forgot password?</a>
              </div>
            </div>

            <p id="error" :ref="error" v-if="error" class="font-semibold text-mc-red text-sm">{{ error }}</p>

            <div>
              <button id="login" type="submit" :disabled="isLoading" class="w-full inline-flex justify-center items-center rounded-pill border-2 border-mc-red bg-mc-red px-4 py-2 text-sm font-bold text-white transition-colors duration-160 hover:bg-mc-red-700 hover:border-mc-red-700 disabled:opacity-60">{{ isLoading ? 'Signing in...' : 'Sign in' }}</button>
            </div>
          </form>

          <!-- PWA Install Link -->
          <div v-if="showPWAPrompt && !isInstalled" class="mt-6 text-center">
            <a
              href="#"
              @click.prevent="installPWA"
              class="text-sm font-semibold text-mc-red hover:text-mc-red-700 transition-colors duration-160"
            >
              📱 Install MeshPortal App
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
  
<script>
//Import the necessary tools.
import { ref, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc, query, where, or, getDocs, collection } from 'firebase/firestore';
import { auth, db } from '@/firebase';

export default {
  name: 'LoginMain',
  setup() {
    // Set up all the variable references.
    const email = ref('');
    const password = ref('');
    const isLoading = ref(false);
    const error = ref('');
    const showPWAPrompt = ref(false);
    const isInstalled = ref(false);

    // Grab stored values
    const store = useStore();
    const router = useRouter();
    
    /**
     * Check if app is already installed
     */
    const checkIfInstalled = () => {
      // First check if running in standalone mode (currently in installed app)
      const isStandalone = 
        (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
        (window.navigator.standalone === true) ||
        (window.matchMedia && window.matchMedia('(display-mode: minimal-ui)').matches) ||
        (window.matchMedia && window.matchMedia('(display-mode: fullscreen)').matches);

      if (isStandalone) {
        isInstalled.value = true;
        // Mark that app is installed using localStorage so browser knows
        localStorage.setItem('pwa-ever-installed', 'true');
        localStorage.setItem('pwa-last-standalone-check', Date.now().toString());
        return true;
      }

      // If in browser, check if app was ever installed on this device
      const wasEverInstalled = localStorage.getItem('pwa-ever-installed') === 'true';
      const lastCheck = localStorage.getItem('pwa-last-standalone-check');
      
      // Only trust recent standalone checks (within last 24 hours)
      if (wasEverInstalled && lastCheck) {
        const timeSinceLastCheck = Date.now() - parseInt(lastCheck);
        const hoursAgo = timeSinceLastCheck / (1000 * 60 * 60);
        
        // Only consider installed if used in standalone mode within last 24 hours
        if (hoursAgo < 24) {
          isInstalled.value = true;
          return true;
        } else {
          localStorage.removeItem('pwa-ever-installed');
          localStorage.removeItem('pwa-last-standalone-check');
          isInstalled.value = false;
          return false;
        }
      }

      isInstalled.value = false;
      return false;
    };

    /**
     * Initialize PWA prompt on mount
     */
    onMounted(() => {
      // IMPORTANT: Check if beforeinstallprompt already fired (stored in window.deferredPrompt)
      if (window.deferredPrompt) {
        localStorage.removeItem('pwa-ever-installed');
        localStorage.removeItem('pwa-last-standalone-check');
        isInstalled.value = false;
        showPWAPrompt.value = true;
      }
      
      // Check if already installed
      checkIfInstalled();
      
      // Listen for beforeinstallprompt event
      // If this fires, it means app is NOT installed (or was uninstalled)
      window.addEventListener('beforeinstallprompt', (e) => {
        // Clear installed flags since this event only fires when app is NOT installed
        localStorage.removeItem('pwa-ever-installed');
        localStorage.removeItem('pwa-last-standalone-check');
        
        // Update state
        isInstalled.value = false;
        showPWAPrompt.value = true;
        
        // Store prompt for later use
        window.deferredPrompt = e;
        e.preventDefault();
      });
      
      // Set up interval to check install status periodically
      const checkInterval = setInterval(() => {
        const wasInstalled = isInstalled.value;
        const nowInstalled = checkIfInstalled();
        
        // If installation status changed, update UI
        if (wasInstalled !== nowInstalled) {
          isInstalled.value = nowInstalled;
          showPWAPrompt.value = !nowInstalled;
        }
      }, 2000);
      
      // Always show link if not installed (ignore dismiss state)
      if (!isInstalled.value) {
        showPWAPrompt.value = true;
      }
      
      // Listen for appinstalled event to hide the link
      window.addEventListener('appinstalled', () => {
        isInstalled.value = true;
        showPWAPrompt.value = false;
        
        // Mark as installed in localStorage for cross-context detection
        localStorage.setItem('pwa-ever-installed', 'true');
        localStorage.setItem('pwa-last-standalone-check', Date.now().toString());
      });
      
      // Cleanup on unmount
      return () => {
        clearInterval(checkInterval);
      };
    });

    /**
     * Trigger PWA installation
     */
    const installPWA = async () => {
      if (!window.deferredPrompt) {
        showManualInstallInstructions();
        return;
      }

      try {
        window.deferredPrompt.prompt();
        const { outcome } = await window.deferredPrompt.userChoice;
        if (outcome !== 'accepted') {
          // Don't hide the link - let them try again later
        }
        window.deferredPrompt = null;
      } catch (error) {
        console.error('PWA install failed:', error);
        showManualInstallInstructions();
      }
    };

    /**
     * Show manual install instructions based on platform
     */
    const showManualInstallInstructions = () => {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const isAndroid = /Android/.test(navigator.userAgent);
      const isEdge = /Edge|Edg/.test(navigator.userAgent);
      
      let message = '';
      
      if (isIOS) {
        message = 'To install this app on iOS:\n\n1. Tap the Share button (square with arrow)\n2. Scroll down and select "Add to Home Screen"\n3. Tap "Add"';
      } else if (isAndroid) {
        message = 'To install this app:\n\n1. Tap the menu button (⋮) in your browser\n2. Select "Add to Home Screen" or "Install App"\n3. Tap "Install"';
      } else if (isEdge) {
        message = 'To install this app in Edge:\n\n1. Click the menu (⋯) → Apps → "Install this site as an app"\n2. Or look for the install icon in the address bar';
      } else {
        message = 'To install this app:\n\n1. Look for the install icon in your browser\'s address bar\n2. Or check your browser\'s menu for "Install" option';
      }
      
      alert(message);
    };
    
    /**
     * Initiates when the login button is clicked. It sends an authentiaction
     * querry to firebase and if sucessfull, grabs user info from the database
     * and saves it.
     */
    const login = async () => {

      // Deactivate the login button and have it display "Loging in..."
      isLoading.value = true;

      // Send an authentication request and navigate the user to the Home page
      // if successfull.
      try {
        await authenticate(email.value, password.value);
        router.push({ name: 'Dashboard' });

      // The authentication failed. Display the proper exception message.
      } catch (exception) {

        // Activate the login button and have it display "Login"
        isLoading.value = false;

        if (exception.code === "auth/invalid-credential") {
          error.value = "Email or Password is invalid";

        } else if (exception.code === "auth/too-many-requests") {
          error.value = "Too many attempts were made and the account has been disabled. Please try again later.";

        } else {
          error.value = exception.code || "An Unexpected error occurred";
        }
      }
    };

    /**
     * Sends and authentication request to firebase. If successful, it grabs the users
     * credentials and stores it.
     * @param   {String}  email     The email address of the user.
     * @param   {String}  password  The password associated with the email.
     */
    const authenticate = async (email, password) => {

      // Send the authentication request and grab the users credentials.
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      // Using the users UID, grab the document containing the users information.
      const userRef = doc(db, "users", userCredential.user.uid);
      const users = await getDoc(userRef);
      let userData = {};

      // Store the users information.
      if (users.exists()) {
        userData = users.data();
        userData.entityData = {};
        userData.uid = users.id;

        const entityRef = doc(db, "entities", userData.entity);
        const entity = await getDoc(entityRef);
        if (entity.exists()) {
          userData.entityData[entity.id] = entity.data();
          userData.entityViewing = [entity.id]
        }

        if (userData.entityData[userData.entity].subEntities?.length > 0) {
          for (let subEntity of userData.entityData[userData.entity].subEntities) {
            const entityRef = doc(db, "entities", subEntity);
            const entity = await getDoc(entityRef);
            if (entity.exists()) {
              userData.entityData[entity.id] = entity.data();
            }
          }
        }
        store.dispatch('setUser', userData);
      } else {
        console.error('User document not found for uid:', userCredential.user.uid);
      }

      // Get all the applicable apps from the database
      const appsRef = query(
        collection(db, "apps"),
        or(
          where("allAccess", "==", true),
          where("entities", "array-contains", userData.entity)
        )
      );
      const apps = await getDocs(appsRef);
      store.dispatch('setApps', apps.docs.map(doc => ({id: doc.id, ...doc.data()})));
    };

    /**
     * Initiates when the "Forgot Password" link is clicked. It sends the user
     * to the "/reset-password" page.
     */
    const forgotPassword = () => {
      router.push({ name: 'ResetPassword' });
    };

    return {
      email,
      password,
      isLoading,
      error,
      showPWAPrompt,
      isInstalled,
      login,
      forgotPassword,
      installPWA,
    };
  },
};
</script>

<style>
.login-background {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 98vh;
  background-image: url('@/assets/images/MeshComm-Hero-Area-SIGNAL.jpg');
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
}
</style>
